import { isNameField, toTitleCase } from './nameCase';
import { wrapLines } from './badgeCanvas';

export const TEXT_CASES = [
  { value: 'auto', label: 'Auto (capitalise lowercase words)' },
  { value: 'title', label: 'Title Case' },
  { value: 'upper', label: 'UPPERCASE' },
  { value: 'none', label: 'As entered' },
];

// IDs, phone numbers, emails and links are printed exactly as stored.
const LOOKS_LIKE_CODE = /[\d@]|:\/\//;

/** "new delhi" -> "New Delhi" while "AIIMS delhi" -> "AIIMS Delhi": only all-lowercase words change. */
const capitaliseLowercaseWords = (value) => value.replace(/(^|[\s\-/("])(\p{Ll}[\p{Ll}'.]*)/gu,
  (_, sep, word) => sep + word.charAt(0).toUpperCase() + word.slice(1));

export function applyTextCase(value, field) {
  if (typeof value !== 'string') return value;
  switch (field?.style?.textCase || 'auto') {
    case 'title': return toTitleCase(value);
    case 'upper': return value.toUpperCase();
    case 'none': return value;
    default:
      if (isNameField(field?.key)) return toTitleCase(value);
      return LOOKS_LIKE_CODE.test(value) ? value : capitaliseLowercaseWords(value.trim());
  }
}

const CSS_PX_PER_IN = 96;
// Badge font sizes are points (as printed); previews draw them in CSS px.
export const PX_PER_PT = 96 / 72;
const LINE_HEIGHT = 1.2;
const MIN_SCALE = 0.6;

let measureContext;
const defaultMeasure = (text, fontPx, field) => {
  if (measureContext === undefined) {
    try {
      measureContext = document.createElement('canvas').getContext('2d');
    } catch (_) {
      measureContext = null;
    }
  }
  if (!measureContext) return null;
  measureContext.font = `${field.style?.fontWeight || 'normal'} ${fontPx}px ${field.style?.fontFamily || 'Arial, sans-serif'}`;
  return measureContext.measureText(text).width;
};

export const TEXT_OVERFLOW = [
  { value: 'wrap', label: 'Wrap to the next line' },
  { value: 'shrink', label: 'One line, shrink font' },
];

export const textOverflowMode = (field) => (field?.style?.overflow === 'shrink' ? 'shrink' : 'wrap');

/** Smallest size (pt) a long value may shrink to: the field's setting, else 60% of its size. */
export function minFontSizeOf(field) {
  const base = field?.style?.fontSize || 14;
  const configured = Number(field?.style?.minFontSize);
  return configured > 0 ? Math.min(configured, base) : base * MIN_SCALE;
}

/** Lines the value occupies at a size: one line in shrink mode, word-wrapped otherwise. */
export function textLines(text, field, maxWidth, measureWidth) {
  return textOverflowMode(field) === 'shrink' ? [String(text)] : wrapLines(text, maxWidth, measureWidth);
}

/**
 * Configured font size (pt) unless the text cannot fit its box; then the largest size that fits,
 * never below the field's minimum. "wrap" may use several lines, "shrink" keeps one line.
 * Short text in a big box is never shrunk.
 */
export function fitFontSize(text, field, measure = defaultMeasure) {
  const base = field?.style?.fontSize || 14;
  if (!text || typeof text !== 'string' || !field?.width || !field?.height) return base;
  const boxWidth = field.width * CSS_PX_PER_IN;
  const boxHeight = field.height * CSS_PX_PER_IN;
  if (measure('M', base * PX_PER_PT, field) === null) return base;
  const floor = minFontSizeOf(field);

  for (let size = base; size >= floor; size -= 0.5) {
    const fontPx = size * PX_PER_PT;
    const lines = textLines(text, field, boxWidth, (t) => measure(t, fontPx, field));
    const fitsWidth = lines.every((line) => measure(line, fontPx, field) <= boxWidth);
    if (fitsWidth && lines.length * fontPx * LINE_HEIGHT <= boxHeight) return size;
  }
  return floor;
}

/** Inline CSS for a text box so long values wrap or stay on one line instead of being cropped at both ends. */
export function textBoxCss(field) {
  return textOverflowMode(field) === 'shrink'
    ? 'white-space: nowrap; overflow: hidden; text-overflow: clip;'
    : 'white-space: normal; overflow-wrap: anywhere; word-break: break-word; overflow: visible;';
}
